import {models} from 'powerbi-client'

const unileverFilterNames = ['RegionalUL', 'FormatoUL', 'TamanhoUL', 'Ecoa', 'E-coa']

function normalizeFilterName(value: unknown): string {
  if (typeof value !== 'string') return ''
  // Normalize only for comparison; preserve the SDK's original filter target.
  return value
    .normalize('NFKD')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, '')
}

const unileverFilterTerms = unileverFilterNames.map(normalizeFilterName)

function containsUnileverTerm(value: unknown): boolean {
  const name = normalizeFilterName(value)
  return unileverFilterTerms.some((term) => name.includes(term))
}

function hasUnileverTarget(target: unknown): boolean {
  if (Array.isArray(target)) return target.some(hasUnileverTarget)
  if (!target || typeof target !== 'object') return false
  const fields = target as Record<string, unknown>
  return (
    ['column', 'measure', 'hierarchy', 'hierarchyLevel'].some((field) =>
      containsUnileverTerm(fields[field])
    ) ||
    (Array.isArray(fields.groupedColumns) && fields.groupedColumns.some(hasUnileverTarget))
  )
}

export function applyUnileverFilterVisibility<T extends models.IFilter>(
  filters: T[],
  canViewUnileverFilters: boolean
): T[] {
  return filters.map((filter) => {
    if (
      !hasUnileverTarget(filter.target) &&
      !containsUnileverTerm(filter.displaySettings?.displayName)
    ) {
      return filter
    }

    const isHiddenInViewMode = !canViewUnileverFilters
    const currentlyHidden = filter.displaySettings?.isHiddenInViewMode === true
    if (currentlyHidden === isHiddenInViewMode) return filter

    return {
      ...filter,
      displaySettings: {...filter.displaySettings, isHiddenInViewMode},
    }
  })
}

interface FilterScope {
  getFilters: () => Promise<models.IFilter[]>
  updateFilters: (
    operation: models.FiltersOperations,
    filters: models.IFilter[]
  ) => Promise<unknown>
}

export async function syncUnileverFilterVisibility(
  scope: FilterScope,
  canViewUnileverFilters: boolean,
  isConnected: () => boolean
) {
  if (!isConnected()) return
  const filters = await scope.getFilters()
  if (!isConnected()) return

  const visibleFilters = applyUnileverFilterVisibility(filters, canViewUnileverFilters)
  const changedFilters = visibleFilters.filter((filter, index) => filter !== filters[index])
  if (changedFilters.length) {
    // Update only existing filter cards; keep their values and all unrelated filters.
    await scope.updateFilters(models.FiltersOperations.Replace, changedFilters)
  }
  return isConnected() ? visibleFilters : undefined
}
